import { toast } from '@heroui/react'
import { v4 as uuid } from 'uuid'
import { create } from 'zustand'

import { driversApi } from '@/api/drivers.api'
import i18n from '@/i18n'
import type { Settlement } from '@/types/settlement'
import { getAppSettings } from '@/utils/settings'
import { SortDirection, sorting, type TableSort } from '@/utils/sorting'

interface PaymentsStore {
  settlements: Settlement[];
  currentSettlement: Settlement | null;
  isLoading: boolean;
  error: string | null;
  filters: {
    driverID: string;
    phone: string;
  };
  pagination: {
    sort: TableSort | null;
    page: number;
    limit: number;
    totalPages: number;
    total: number;
  };

  fetchSettlements: () => Promise<void>;
  fetchDriverSettlement: (driverId: string, date?: string) => Promise<void>;

  addPayment: (driverId: string, data: { date: string; amount: number }) => Promise<void>;
  updatePayment: (driverId: string, paymentId: string, data: { date: string; amount: number }) => Promise<void>;
  deletePayment: (driverId: string, paymentId: string) => Promise<void>;

  addInitialBalance: (driverId: string, data: { date: string; amount: number }) => Promise<void>;
  updateInitialBalance: (driverId: string, itemId: string, data: { date: string; amount: number }) => Promise<void>;
  deleteInitialBalance: (driverId: string, itemId: string) => Promise<void>;

  setFilters: (filters: Partial<PaymentsStore['filters']>) => void;
  setPagination: (pagination: Partial<PaymentsStore['pagination']>) => void;
  resetFilters: () => void;
  clearError: () => void;
}

export const usePaymentsStore = create<PaymentsStore>((set, get) => ({
  settlements: [],
  currentSettlement: null,
  isLoading: false,
  error: null,
  filters: {
    driverID: '',
    phone: '',
  },
  pagination: {
    sort: { key: 'name', direction: SortDirection.ASC },
    page: 0,
    limit: getAppSettings().driversLimit ?? 10,
    totalPages: 0,
    total: 0,
  },

  fetchSettlements: async () => {
    const { filters, pagination } = get()
    set({ isLoading: true, error: null })

    const params = {
      driverID: filters.driverID || undefined,
      page: pagination.page,
      limit: pagination.limit,
      sort: pagination.sort ? [sorting(pagination.sort)] : undefined,
    }

    try {
      const response = await driversApi.getDriversSettlements(params)
      // Filter driver list client-side if query is present, since API doesn't support filter params yet
      let list = response.Settlements ?? []

      list = list.map(settlement => {
        const serverHasOpening = settlement.Items?.some(item => item.Type === 'opening_balance')
        let total = settlement.Total
        if (!serverHasOpening) {
          const localBalance = localStorage.getItem(`opening_balance_${settlement.Driver.ID}`)
          if (localBalance) {
            const { Balance } = JSON.parse(localBalance)
            total += Balance
          }
        }
        return {
          ...settlement,
          Total: total,
        }
      })

      if (filters.driverID) {
        list = list.filter(s => s.Driver.ID === filters.driverID)
      }
      if (filters.phone) {
        list = list.filter(s => (s.Driver.Phone ?? '').includes(filters.phone))
      }

      set({
        settlements: list,
        pagination: {
          ...pagination,
          totalPages: filters.driverID || filters.phone ? 1 : Math.ceil(response.Pagination.Total / pagination.limit),
          total: filters.driverID || filters.phone ? list.length : response.Pagination.Total,
        },
      })
    } catch (err) {
      const msg = (err as Error).message
      set({ error: msg })
      toast.danger(i18n.t('settlements.toast.fetch_error'), { description: msg })
    } finally {
      set({ isLoading: false })
    }
  },

  fetchDriverSettlement: async (driverId, date) => {
    set({ isLoading: true, error: null })
    try {
      const response = await driversApi.getDriverSettlement(driverId, date ? { date } : undefined)
      const localItems: any[] = []
      const serverHasOpening = response.Items?.some(item => item.Type === 'opening_balance')
      if (!serverHasOpening) {
        const localBalance = localStorage.getItem(`opening_balance_${driverId}`)
        if (localBalance) {
          const { Balance, Date: balDate } = JSON.parse(localBalance)
          if (!date || balDate <= date) {
            localItems.push({
              ID: '00000000-0000-0000-0000-000000000000',
              Name: 'Начальный баланс',
              Type: 'opening_balance' as const,
              StartDate: balDate,
              EndDate: balDate,
              Total: Balance,
            })
          }
        }
      }
      const mergedItems = [...(response.Items ?? []), ...localItems]

      let calculatedTotal = 0
      for (const item of mergedItems) {
        if (item.Type === 'payment') {
          calculatedTotal += item.Amount
        } else if (item.Type === 'advance_report') {
          calculatedTotal -= item.Total
        } else if (item.Type === 'opening_balance') {
          calculatedTotal += item.Total
        }
      }

      set({
        currentSettlement: {
          ...response,
          Items: mergedItems,
          Total: calculatedTotal,
        },
      })
    } catch (err) {
      const msg = (err as Error).message
      set({ error: msg })
      toast.danger(i18n.t('settlements.toast.fetch_detail_error'), { description: msg })
    } finally {
      set({ isLoading: false })
    }
  },

  addPayment: async (driverId, data) => {
    set({ isLoading: true, error: null })
    try {
      const paymentId = uuid()
      await driversApi.createDriverPayment(driverId, {
        ID: paymentId,
        Name: 'Payment',
        Date: data.date,
        Amount: data.amount,
      })
      toast.success(i18n.t('settlements.toast.payment_create_success'))
    } catch (err) {
      const msg = (err as Error).message
      set({ error: msg })
      toast.danger(i18n.t('settlements.toast.payment_create_error'), { description: msg })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  updatePayment: async (driverId, paymentId, data) => {
    set({ isLoading: true, error: null })
    try {
      await driversApi.updateDriverPayment(driverId, paymentId, {
        ID: paymentId,
        Name: 'Payment',
        Date: data.date,
        Amount: data.amount,
      })
      toast.success(i18n.t('settlements.toast.payment_update_success'))
    } catch (err) {
      const msg = (err as Error).message
      set({ error: msg })
      toast.danger(i18n.t('settlements.toast.payment_update_error'), { description: msg })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  deletePayment: async (driverId, paymentId) => {
    set({ isLoading: true, error: null })
    try {
      await driversApi.deleteDriverPayment(driverId, paymentId)
      toast.success(i18n.t('settlements.toast.payment_delete_success'))
    } catch (err) {
      const msg = (err as Error).message
      set({ error: msg })
      toast.danger(i18n.t('settlements.toast.payment_delete_error'), { description: msg })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  addInitialBalance: async (driverId, data) => {
    set({ isLoading: true, error: null })
    try {
      try {
        await driversApi.updateDriverBalance(driverId, { Balance: data.amount, Date: data.date })
      } catch (apiErr) {
        console.warn('API call failed, falling back to localStorage:', apiErr)
        const localBalance = { Balance: data.amount, Date: data.date }
        localStorage.setItem(`opening_balance_${driverId}`, JSON.stringify(localBalance))
      }
      toast.success(i18n.t('settlements.toast.initial_balance_create_success'))
    } catch (err) {
      const msg = (err as Error).message
      set({ error: msg })
      toast.danger(i18n.t('settlements.toast.initial_balance_create_error'), { description: msg })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  updateInitialBalance: async (driverId, _itemId, data) => {
    set({ isLoading: true, error: null })
    try {
      try {
        await driversApi.updateDriverBalance(driverId, { Balance: data.amount, Date: data.date })
      } catch (apiErr) {
        console.warn('API call failed, falling back to localStorage:', apiErr)
        const localBalance = { Balance: data.amount, Date: data.date }
        localStorage.setItem(`opening_balance_${driverId}`, JSON.stringify(localBalance))
      }
      toast.success(i18n.t('settlements.toast.initial_balance_update_success'))
    } catch (err) {
      const msg = (err as Error).message
      set({ error: msg })
      toast.danger(i18n.t('settlements.toast.initial_balance_update_error'), { description: msg })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  deleteInitialBalance: async (driverId, _itemId) => {
    set({ isLoading: true, error: null })
    try {
      try {
        await driversApi.updateDriverBalance(driverId, { Balance: 0, Date: '2026-01-01' })
      } catch (apiErr) {
        console.warn('API call failed, falling back to localStorage:', apiErr)
      }
      localStorage.removeItem(`opening_balance_${driverId}`)
      toast.success(i18n.t('settlements.toast.initial_balance_delete_success'))
    } catch (err) {
      const msg = (err as Error).message
      set({ error: msg })
      toast.danger(i18n.t('settlements.toast.initial_balance_delete_error'), { description: msg })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  setFilters: filters => {
    set(state => ({
      filters: { ...state.filters, ...filters },
      pagination: { ...state.pagination, page: 0 },
    }))
    get().fetchSettlements()
  },

  setPagination: pagination => {
    set(state => ({
      pagination: { ...state.pagination, ...pagination },
    }))
    get().fetchSettlements()
  },

  resetFilters: () => {
    set({
      filters: { driverID: '', phone: '' },
      pagination: {
        ...get().pagination,
        page: 0,
      },
    })
    get().fetchSettlements()
  },

  clearError: () => set({ error: null }),
}))
